import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { cn } from '@/lib/utils';

import { useCreatePaymentDetails } from '../../hooks/payment-details/use-create-payment-details';
import { useUpdatePaymentDetails } from '../../hooks/payment-details/use-update-payment-details';
import { PaymentDetailsEntry } from '../../types/payment-details';
import {
  PAYMENT_DETAILS_TEXT_MAX_LENGTH,
  PaymentDetailsFormValues,
  paymentDetailsSchema,
} from '../../validations/payment-details-schema';

const I18N = 'pages.finance.paymentDetails';

const FORM_FIELD_NAME = 'name';
const FORM_FIELD_DETAILS = 'details';
const FORM_FIELD_IS_DEFAULT = 'isDefault';

const EMPTY_VALUES: PaymentDetailsFormValues = {
  [FORM_FIELD_NAME]: '',
  [FORM_FIELD_DETAILS]: '',
  [FORM_FIELD_IS_DEFAULT]: false,
};

interface PaymentDetailsFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Entry being edited; null = create mode. */
  entry: PaymentDetailsEntry | null;
  /** Loaded entries — used for an instant client-side duplicate-name check. The backend
   *  PAYMENT_DETAILS_NAME_DUPLICATE code is the authoritative check (handles races) and
   *  surfaces on the same field via the mutations' `onDuplicateName`. */
  existingEntries: PaymentDetailsEntry[];
}

export const PaymentDetailsFormModal = ({
  open,
  onClose,
  entry,
  existingEntries,
}: PaymentDetailsFormModalProps) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  // The very first entry an organization creates has nothing to default against —
  // force it as the default rather than let the org end up with none.
  const isForcedDefault = entry === null && existingEntries.length === 0;

  // The org's current default, if it's a different entry than the one being edited —
  // used to warn that saving with the checkbox on will replace it.
  const currentOtherDefault = existingEntries.find(
    existing => existing.isDefault && existing.id !== entry?.id
  );

  const form = useForm<PaymentDetailsFormValues>({
    resolver: zodResolver(paymentDetailsSchema),
    mode: 'onChange',
    defaultValues: EMPTY_VALUES,
  });

  // Re-seed the form each time the modal opens for a different entry (or for create).
  useEffect(() => {
    if (open) {
      form.reset(
        entry
          ? { name: entry.name, details: entry.details, isDefault: entry.isDefault }
          : { ...EMPTY_VALUES, [FORM_FIELD_IS_DEFAULT]: isForcedDefault }
      );
    }
  }, [open, entry, isForcedDefault, form]);

  const onDuplicateName = () =>
    form.setError(FORM_FIELD_NAME, {
      message: `authenticated.settings:${I18N}.validation.nameDuplicate`,
    });

  const createMutation = useCreatePaymentDetails({ onSuccess: onClose, onDuplicateName });
  const updateMutation = useUpdatePaymentDetails({ onSuccess: onClose, onDuplicateName });
  const isPending = createMutation.isPending || updateMutation.isPending;

  const detailsLength = form.watch(FORM_FIELD_DETAILS)?.length ?? 0;

  const { ref: scrollRef, isScrollable } = useScrollOverflow();

  const handleSubmit = form.handleSubmit(values => {
    const isDuplicateName = existingEntries.some(
      existing =>
        existing.id !== entry?.id &&
        existing.name.trim().toLowerCase() === values.name.trim().toLowerCase()
    );

    if (isDuplicateName) {
      form.setError(FORM_FIELD_NAME, {
        message: `authenticated.settings:${I18N}.validation.nameDuplicate`,
      });
      return;
    }

    if (entry) {
      updateMutation.mutate({ id: entry.id, ...values });
    } else {
      createMutation.mutate(values);
    }
  });

  return (
    <Modal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      preventClose={isPending}
      className="flex max-h-[85vh] max-w-xl flex-col p-0"
    >
      <Modal.Title className={cn('shrink-0 px-6 pb-4 pt-6 mb-0', isScrollable && 'border-b')}>
        {t(
          entry
            ? `authenticated.settings:${I18N}.modal.editTitle`
            : `authenticated.settings:${I18N}.modal.createTitle`
        )}
      </Modal.Title>

      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-1p-ignore
          autoComplete="off"
        >
          <Modal.Body ref={scrollRef} className="space-y-4 overflow-y-auto px-6 py-4">
            <FormField
              control={form.control}
              name={FORM_FIELD_NAME}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`authenticated.settings:${I18N}.modal.fields.name.label`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        `authenticated.settings:${I18N}.modal.fields.name.placeholder`
                      )}
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={FORM_FIELD_DETAILS}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`authenticated.settings:${I18N}.modal.fields.details.label`)}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={6}
                      maxLength={PAYMENT_DETAILS_TEXT_MAX_LENGTH}
                      placeholder={t(
                        `authenticated.settings:${I18N}.modal.fields.details.placeholder`
                      )}
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormControl>
                  <div className="flex items-start justify-between gap-2">
                    <FormMessage />
                    <span className="ml-auto text-xs text-muted-foreground">
                      {t(`authenticated.settings:${I18N}.modal.counter`, {
                        count: detailsLength,
                        max: PAYMENT_DETAILS_TEXT_MAX_LENGTH,
                      })}
                    </span>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={FORM_FIELD_IS_DEFAULT}
              render={({ field }) => (
                <>
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isForcedDefault}
                      />
                    </FormControl>
                    <FormLabel
                      className={cn(
                        'font-normal',
                        isForcedDefault ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      {t(`authenticated.settings:${I18N}.modal.defaultCheckbox`)}
                    </FormLabel>
                    {isForcedDefault && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipPortal>
                          <TooltipContent className="max-w-xs">
                            <p>{t(`authenticated.settings:${I18N}.modal.forcedDefaultTooltip`)}</p>
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    )}
                  </FormItem>
                  {entry?.isDefault && !field.value && (
                    <Alert variant="warning">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {t(`authenticated.settings:${I18N}.modal.unsetDefaultWarning`)}
                      </AlertDescription>
                    </Alert>
                  )}
                  {field.value && currentOtherDefault && (
                    <Alert variant="info">
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        <Trans
                          t={t}
                          i18nKey={`authenticated.settings:${I18N}.modal.replaceDefaultInfo`}
                          values={{ name: currentOtherDefault.name }}
                          components={{ strong: <strong /> }}
                        />
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            />
          </Modal.Body>

          <Modal.Footer className={cn('shrink-0 mt-0 px-6 pb-6 pt-4', isScrollable && 'border-t')}>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t('common:cancel')}
            </Button>
            <ButtonLoadable type="submit" isLoading={isPending} disabled={!form.formState.isValid}>
              {entry ? t('common:save') : t(`authenticated.settings:${I18N}.modal.createButton`)}
            </ButtonLoadable>
          </Modal.Footer>
        </form>
      </Form>
    </Modal>
  );
};
